import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, trackPageView } from '../utils/analytics';
import { usePageMeta } from '../utils/usePageMeta';
import { SubPageNav, SubPageFoot, ArrowIcon, BOOK_URL } from '../components/SubPageChrome';
import introHero from '../assets/tempo_player1.webp';
import { MORNING_EVENTS } from '../constants/morning';
import '../pages/HomePage.css';

const PLAYTOMIC_URL =
  'https://app.playtomic.io/tenants/8466df30-5b02-492a-8cda-7bd631040099/academy';

const SLOTS = MORNING_EVENTS.filter((event) => event.eventType === 'free_intro')
  .map((event) => ({ id: event.id, weekday: event.day, time: event.time }));

const FAQS: { q: string; a: React.ReactNode }[] = [
  { q: 'Do I need gear?', a: 'Paddle and balls are on the house. Bring athletic shoes with non-marking soles.' },
  { q: 'What should I wear?', a: "Anything you'd wear to the gym. Courts are outdoors, so check the weather." },
  { q: 'Can I bring a friend?', a: 'Yes — padel is doubles by nature. Register each player separately so everyone has a guaranteed spot.' },
  { q: 'Is it really free?', a: <>Really free. One free intro per person — after that, book any court from the <a href="https://app.playtomic.io/tenants/8466df30-5b02-492a-8cda-7bd631040099" target="_blank" rel="noopener noreferrer">Playtomic app</a>.</> },
  { q: 'How long is the session?', a: 'Wednesday morning is 45 minutes. Evening and Saturday sessions are 60 minutes: rules, strokes, positioning, then real rallies on glass.' },
  { q: 'Where do I park?', a: 'Free parking on-site at 1729 Brittmoore Rd Ste A, Houston, TX 77043.' },
];

const FreeIntroPadel: React.FC = () => {
  const [selected, setSelected] = useState<string>('wed-9am');

  usePageMeta({
    title: 'Free Intro to Padel in Houston | TEMPO Padel & Pickleball Club',
    description: 'Try padel free at TEMPO in Houston. Coach-led intro clinics, gear included. Reserve your free spot.',
    canonicalPath: '/free-intro',
  });

  useEffect(() => {
    document.title = 'Free Intro to Padel in Houston | TEMPO Padel & Pickleball Club';
    trackPageView('Free Intro to Padel', '/free-intro');
    const meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const desc = 'Try padel free at TEMPO in Houston. Coach-led intro clinics. Gear included. Reserve your free spot.';
    if (meta) meta.content = desc;
  }, []);

  const slotHref = useMemo(() => PLAYTOMIC_URL, []);

  const handleSlotClick = (slot: typeof SLOTS[number]) => {
    setSelected(slot.id);
    trackEvent('free_intro_slot_click', { slot_id: slot.id, weekday: slot.weekday, time: slot.time });
  };

  return (
    <div className="home-v2">
      <SubPageNav ctaLabel="Sign Up" ctaHref="#register" active="free-intro" />

      <section className="v2-page-hero">
        <div className="v2-container">
          <div className="kicker">01 / Free Intro to Padel</div>
          <h1>
            Your first <em>session</em> is on us.
          </h1>
          <p className="lede">
            No gear, no experience, no excuses. We'll put a paddle in your hand, teach
            you the rules, and by the end of the session you'll be rallying. It's the
            fastest introduction to the fastest-growing sport in the world.
          </p>
        </div>
      </section>

      <section className="v2-section noborder">
        <div className="v2-container">
          <div className="v2-intro-grid">
            <div className="v2-steps">
              <div className="v2-step">
                <span className="n">01</span>
                <span className="t">
                  Pick a slot
                  <span>Wednesdays, 9:00–9:45 AM or 6:00–7:00 PM. Thursdays, 6:00–7:00 PM. Saturdays, 9:00–10:00 AM. All times are local to Houston.</span>
                </span>
                <span className="time">2 MIN</span>
              </div>
              <div className="v2-step">
                <span className="n">02</span>
                <span className="t">
                  Show up
                  <span>Athletic shoes. Paddle and balls are on us.</span>
                </span>
                <span className="time">5 MIN</span>
              </div>
              <div className="v2-step">
                <span className="n">03</span>
                <span className="t">
                  Learn the basics
                  <span>Rules, strokes, positioning - all in 45–60 minutes.</span>
                </span>
                <span className="time">15–30 MIN</span>
              </div>
              <div className="v2-step">
                <span className="n">04</span>
                <span className="t">
                  Play a real point
                  <span>By the end, you're rallying on glass.</span>
                </span>
                <span className="time">30 MIN</span>
              </div>
            </div>
            <div className="visual">
              <img src={introHero} alt="Padel player at TEMPO" />
            </div>
          </div>
        </div>
      </section>

      <section className="v2-section" id="register" style={{ background: 'var(--paper)' }}>
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">02 /</span>
              <span className="label">Reserve Your Spot</span>
            </div>
            <h2>
              Pick a <em>time.</em>
            </h2>
          </div>

          <div style={{ marginBottom: 32 }}>
            <div className="mono" style={{ color: 'var(--muted)', marginBottom: 12 }}>
              Available slots this week · Houston local time
            </div>
            <div className="v2-slot-grid">
              {SLOTS.map((slot) => (
                <a
                  key={slot.id}
                  href={slotHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`v2-slot ${selected === slot.id ? 'selected' : ''}`}
                  onClick={() => handleSlotClick(slot)}
                >
                  <span className="d">{slot.weekday}</span>
                  <span className="t">{slot.time}</span>
                  <span className="s">See availability</span>
                </a>
              ))}
            </div>
          </div>

          <div className="v2-form-actions">
            <a
              href={slotHref}
              target="_blank"
              rel="noopener noreferrer"
              className="v2-btn v2-btn-on-light"
              onClick={() => trackEvent('free_intro_cta_click', { location: 'register', href: slotHref })}
            >
              Confirm Spot
              <ArrowIcon />
            </a>
            <a href={`tel:+17135706716`} className="v2-btn v2-btn-ghost">
              Text us a different time
            </a>
          </div>
        </div>
      </section>

      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">03 /</span>
              <span className="label">FAQ</span>
            </div>
            <h2>
              Before <em>you</em> come.
            </h2>
          </div>
          <div className="v2-faq-grid">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h4 className="v2-faq-q">{f.q}</h4>
                <p className="v2-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section" id="visit">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">04 /</span>
              <span className="label">After the Lesson</span>
            </div>
            <h2>
              Found your <em>tempo?</em>
            </h2>
          </div>
          <div className="v2-callout-grid">
            <div className="v2-callout">
              <span className="kicker">Mornings at Tempo</span>
              <h3>
                Keep the <em>rhythm.</em>
              </h3>
              <p className="v2-callout-muted">
                Morning open-play and intro slots Wednesday, Thursday, Friday, and Saturday. Beat
                the heat and find your regulars.
              </p>
              <div className="v2-callout-cta">
                <Link to="/mornings" className="v2-btn v2-btn-on-light">
                  See Mornings
                  <ArrowIcon />
                </Link>
              </div>
            </div>
            <div className="v2-callout dark">
              <span className="kicker">Book a court</span>
              <h3>
                Already <em>ready.</em>
              </h3>
              <p className="v2-callout-muted">
                Courts from $12/player/hour. Members save 25% and book 21 days ahead.
              </p>
              <div className="v2-callout-cta">
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="v2-btn v2-btn-primary">
                  Book a Court
                  <ArrowIcon />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SubPageFoot />
    </div>
  );
};

export default FreeIntroPadel;
